import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Page, Text, View, Document, StyleSheet, pdf, Line, Svg, Image } from '@react-pdf/renderer';

import { useAppSelector } from '~redux/hooks';
import { GoldTypeEnum, MetalTypeEnum, TransactionActionEnum } from '~utils/enums';

import AffinLogo from '~assets/images/affin-logo-v1.png';
import ActionButton from '~components/atoms/ActionButton';
import { useDispatch } from 'react-redux';
import combinedActions from '~redux/combinedActions';
import { TBuyOrSellMultipleProductsParams } from '~types/feat/gold';
import Decimal from 'decimal.js';

type PageProps = {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    copy: string;
}

const SummaryPDF = () => {
    const userDetails = useAppSelector(state => state.auth.userDetails);

    const customerTransferSummary = useAppSelector(state => state.customerTransfer.customerTransferSummary);

    const [loading, setLoading] = useState(false);

    const displayValue = (value?: string | number | null) =>
        value === undefined || value === null || value === ''
            ? '-'
            : String(value);

    const summaryTable = [
        { label: 'Transaction Status / Status Transaksi', value: 'Pending' },
        {
            label: 'Transaction Date/Time / Tarikh/Masa Transaksi',
            value: dayjs().format('DD/MM/YYYY hh:mm A'),
        },
        {
            label: 'Branch Name / Nama Cawangan',
            value: displayValue(customerTransferSummary.branchName),
        },
        {
            label: 'From / Dari',
            value: '',
            isHeading: true,
        },
        {
            label: 'Name of Customer/Company/Firm/Association/ Nama Pelanggan/Syarikat/Firma/Persatuan',
            value: displayValue(customerTransferSummary.senderName),
        },
        {
            label: 'Customer ID / ID Pelanggan',
            value: displayValue(customerTransferSummary.senderIdNo),
        },
        {
            label: 'Payment Method / Cara Pembayaran',
            value: '',
        },
        {
            label: 'Quantity / Kuantiti (Gram)',
            value: customerTransferSummary.weight == null ? '-' : Number(customerTransferSummary.weight).toFixed(3),
        },
        {
            label: 'Total Amount / Jumlah Amaun (RM)',
            value: customerTransferSummary.weight == null ? '-' : Number(customerTransferSummary.totalAmount).toFixed(2),
        },
        {
            label: 'To / Kepada',
            value: '',
            isHeading: true,
        },
        {
            label: 'Name of Customer/Company/Firm/Association/ Nama Pelanggan/Syarikat/Firma/Persatuan',
            value: displayValue(customerTransferSummary.receiverName),
        },
        {
            label: 'Account No. / No. Akaun',
            value: '',
        },
        {
            label: 'Quantity / Kuantiti (Gram)',
            value: customerTransferSummary.weight == null ? '-' : Number(customerTransferSummary.weight).toFixed(3),
        },
    ];

    const productTableData = [
        {
            productName: displayValue(customerTransferSummary.productName),
            amount: customerTransferSummary.weight == null ? '-' : `${Number(customerTransferSummary.weight).toFixed(3)} g`,
        },
    ];

    const customerDetails = {
        name: customerTransferSummary.senderName,
        idNo: customerTransferSummary.senderIdNo,
    };
    const PageWithHeaderFooter = ({
        children,
        title,
        subtitle,
        copy,
    }: PageProps) => (
        <Page size='A4' style={copy === 'Customer\'s Copy' ? styles.customerPage : styles.page} orientation='landscape'>
            {/* Header */}
            <View style={styles.header}>
                {
                    copy === 'Customer\'s Copy' &&
                    <Image
                        style={styles.headerLogo}
                        src={AffinLogo}
                    />
                }
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>{title}</Text>
                    <Text style={styles.headerSubtitle}>{subtitle}</Text>
                </View>
                <Text style={styles.headerCopy}>{copy}</Text>
                <View style={styles.divider} />
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {children}
            </View>
        </Page>
    );

    const renderDetails = () => {
        return (
            <View style={styles.detailsTable}>
                {summaryTable.map((item, index) => {
                    if ('isHeading' in item && item.isHeading) {
                        return (
                            <Text
                                key={index}
                                style={styles.sectionHeading}
                            >
                                {item.label}
                            </Text>
                        );
                    }

                    return (
                        <View style={styles.detailsRow} key={index}>
                            <Text style={styles.detailsCell}>
                                {item.label}
                            </Text>

                            <Text style={styles.detailsCell}>
                                : {item.value}
                            </Text>
                        </View>
                    );
                })}
            </View>
        );
    };

    const renderGoldDescription = () => {
        return (
            <View>
                <Text style={styles.goldDescriptionTitle}>Description / Deskripsi</Text>
                <View style={styles.goldTable}>
                    <View style={styles.goldTableRow}>
                        <Text style={styles.goldHeaderCell}>Product / Produk</Text>
                        <Text style={styles.goldHeaderCellPrice}>Price Per Gram / Harga Segram</Text>
                    </View>
                    {
                        productTableData.map((product, index) => {
                            return (
                                <View key={index} style={styles.goldTableRow}>
                                    <Text style={[styles.goldTableCell, { width: '70%' }]}>{product.productName}</Text>
                                    <Text style={[styles.goldTableCell, { width: '30%' }]}>{product.amount}</Text>
                                </View>
                            );
                        })
                    }
                </View>
            </View>
        );
    };

    const renderDeclaration = () => {
        const metalTypeEn = customerTransferSummary.metalType === MetalTypeEnum.Gold
            ? 'Emas Account-I'
            : 'Silver Account-I';

        const metalTypeMalay = customerTransferSummary.metalType === MetalTypeEnum.Gold
            ? 'Akaun Emas-I'
            : 'Akaun Silver-I';
        return (
            <View>
                <Text style={styles.declarationTextTitle}>DECLARATION / DEKLARASI</Text>
                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationText}>1- I/We authorise the Bank to debit the Quantity from my/our Affin {metalTypeEn} for the transaction.</Text>
                    <Text style={styles.declarationText}>Saya/Kami memberi kuasa kepada pihak Bank untuk mendebit Kuantity daripada {metalTypeMalay} AFFIN saya/kami bagi transaksi tersebut.</Text>
                </View>

                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationText}>2- I/We have read, understood and accepted the information as stated in the Product Disclosure Sheet (&quot;PDS&quot;) and Terms & Conditions (&quot;T&C&quot;) and agreed to abide to its terms and conditions which can be downloaded from AFFIN website at www.affinalways.com.</Text>
                    <Text style={styles.declarationText}>Saya/Kami telah membaca, memahami dan menerima maklumat sepenuhnya seperti yang tertera di dalam Lembaran Penerangan Produk (&quot;LPP&quot;) dan Terma & Syarat (&quot;T&S&quot;) dan bersetuju untuk mematuhi terma dan syaratnya yang boleh dimuat turun daripada laman web AFFIN www.affinalways.com.</Text>
                </View>

                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationText}>3- I/We agree to the price and the Bank to proceed with the transaction once it has been approved No cancellation/amendment is allowed.</Text>
                    <Text style={styles.declarationText}>Saya/Kami bersetuju dengan harga dan sebaik sahaja transaksi tersebut telah diluluskan. Bank boleh meneruskan dengan transaksi tersebut. Tiada pembatalan/pindaan dibenarkan.</Text>
                </View>
                
                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationTextBold}>
                        REMINDER: The Customer is reminded to read and understand the terms
                        and conditions of this Deposit of AFFIN {metalTypeEn} Summary Details,
                        the PDS and T&C of AFFIN {metalTypeEn} before signing below.
                        In the event there are any terms and conditions in this Deposit
                        of AFFIN {metalTypeEn} Summary Details, the PDS and T&C of
                        AFFIN {metalTypeEn} that the Customer does not understand,
                        the Customer is hereby advised to seek independent advice
                        and/or discuss further with the Bank's representative before
                        signing below.
                    </Text>

                    <Text style={styles.declarationTextBold}>
                        PERINGATAN: Pelanggan diingatkan untuk membaca dan memahami
                        terma dan syarat dalam Ringkasan Deposit Akaun AFFIN {metalTypeMalay},
                        LPP dan T&S Akaun AFFIN {metalTypeMalay} yang tidak difahami
                        oleh Pelanggan, Pelanggan dengan ini dinasihatkan untuk
                        mendapatkan penjelasan dan berbincang lebih lanjut dengan
                        wakil Bank sebelum menandatangani di bawah.
                    </Text>
                </View>

            </View>
        );
    };

    const renderSignature = () => {
        return (
            <View style={styles.signatureContainer}>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Customer&apos;s Signature / Tandatangan Pelanggan</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                        <Svg height='1' width='500'>
                            <Line
                                x1='5'
                                y1='0'
                                x2='200'
                                y2='0'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Customer/Authorised Signatory Name / Nama Pelanggan/Penandatangan Sah</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>{`: ${customerDetails.name}`}</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>NRIC No. / No. Kad Pengenalan</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>{`: ${customerDetails.idNo}`}</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Date / Tarikh</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Time / Masa</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderExtraSignature = () => {
        return (
            <View style={styles.extraSignature}>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text style={{ fontFamily: 'Helvetica-Bold' }}>(If applicable) / (Jika Berkenaan)</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Customer&apos;s Signature / Tandatangan Pelanggan</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                        <Svg height='1' width='150'>
                            <Line
                                x1='5'
                                y1='0'
                                x2='150'
                                y2='0'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Customer/Authorised Signatory Name / Nama Pelanggan/Penandatangan Sah</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>NRIC No. / No. Kad Pengenalan</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Date / Tarikh</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Time / Masa</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderOfficeInputs = () => {
        return (
            <View style={styles.bankTable}>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankHeaderCell}>FOR BANK&apos;S USE ONLY / UNTUK KEGUNAAN BANK SAHAJA</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <View style={styles.bankTableCell}>
                        <Text>
                            Accepted by (Teller) / Diterima oleh (Juruwang) :
                        </Text>
                        <Svg height='20' width='400'>
                            <Line
                                x1='0'
                                y1='20'
                                x2='200'
                                y2='20'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                    <View style={styles.bankTableCell}>
                        <Text>
                            Verified and Approved by (Officer/Branch Manager) / Disahkan dah Diluluskan oleh (Pegawai/Pengurus Bank) :
                        </Text>
                        <Svg height='20' width='400'>
                            <Line
                                x1='0'
                                y1='20'
                                x2='200'
                                y2='20'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>{`Name / Nama : ${userDetails.displayName}`}</Text>
                    <Text style={styles.bankTableCell}>Name / Nama :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Staff ID / ID Staf :</Text>
                    <Text style={styles.bankTableCell}>Staff ID / ID Staf :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Date / Tarikh :</Text>
                    <Text style={styles.bankTableCell}>Date / Tarikh :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Bank&apos;s Stamp / Cop Bank :</Text>
                    <Text style={styles.bankTableCell}>Bank&apos;s Stamp / Cop Bank :</Text>
                </View>
            </View>
        );
    };

    const handleOpenPDFInNewTab = () => {
        setLoading(true);
        // Create the PDF document

        const docTitle = customerTransferSummary.metalType === MetalTypeEnum.Gold
            ? 'TRANSFER OF AFFIN EMAS ACCOUNT-I / PEMINDAHAN AKAUN AFFIN EMAS-I'
            : 'TRANSFER OF AFFIN SILVER ACCOUNT-I / PEMINDAHAN AKAUN AFFIN SILVER-I';

        const doc = (
            <Document>
                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='SUMMARY DETAILS / RINGKASAN'
                    copy={'Customer\'s Copy'}
                >
                    {renderDetails()}
                    {/* {renderGoldDescription()} */}
                    {renderDeclaration()}
                    {renderSignature()}
                    <View style={styles.extraSignatureContainer}>
                        {renderExtraSignature()}
                        {renderExtraSignature()}
                    </View>
                    {renderOfficeInputs()}
                </PageWithHeaderFooter>

                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='SUMMARY DETAILS / RINGKASAN'
                    copy={'Bank\'s Copy'}
                >
                    {renderDetails()}
                    {/* {renderGoldDescription()} */}
                    {renderDeclaration()}
                    {renderSignature()}
                    <View style={styles.extraSignatureContainer}>
                        {renderExtraSignature()}
                        {renderExtraSignature()}
                    </View>
                    {renderOfficeInputs()}
                </PageWithHeaderFooter>
            </Document>
        );

        // Generate PDF Blob and open in new tab
        pdf(doc).toBlob().then(() => {
            const promise = pdf(doc).toBlob();
            promise.then((blob) => {
                const pdfUrl = URL.createObjectURL(blob);
                setLoading(false);
                window.open(pdfUrl, '_blank');
            }).catch(() => {
                setLoading(false);
            });
        });
    };
    return (
        <div>
            <ActionButton
                label='Print Summary'
                onClick={handleOpenPDFInNewTab}
                isLoading={loading}
            // className={SummaryStyles.container}
            />
        </div>
    );
};

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        paddingTop: 50, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    customerPage: {
        flexDirection: 'column',
        paddingTop: 100, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    header: {
        position: 'absolute',
        top: 10,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerContent: {
        flexDirection: 'column',
        gap: 3,
        marginBottom: 5,
    },
    headerTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerSubtitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerCopy: {
        position: 'absolute',
        top: 5,
        right: 20,
        fontSize: 6,
        color: '#666',
    },
    divider: {
        height: 2,
        backgroundColor: '#000',
        marginVertical: 3,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    footerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    pageNumber: {
        fontSize: 6,
        color: '#666',
    },
    mainContent: {
        flex: 1,
    },
    detailsTable: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 2,
        marginBottom: 15,
    },
    detailsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    detailsCell: {
        width: '50%',
    },
    goldDescriptionTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    goldTable: {
        width: '60%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    goldTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    goldHeaderCell: {
        width: '70%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldHeaderCellPrice: {
        width: '30%', // Second column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldTableCell: {
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    declarationContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 15,
    },
    declarationContainer2: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 5,
    },
    declarationTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    declarationText: {
        fontSize: 6,
    },
    declarationTextTitle: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    declarationTextBold: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
    },
    signatureContainer: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    signatureCell: {
        width: '50%',
    },
    extraSignatureContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontSize: 6,
        gap: 5,
        marginBottom: 15,
    },
    extraSignature: {
        width: '50%',
    },
    extraSignatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    extraSignatureCell: {
        width: '40%',
        maxLines: 1,
    },
    bankTable: {
        width: '100%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    bankTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    bankHeaderCell: {
        width: '100%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    bankTableCell: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 4,
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    sectionHeading: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        marginTop: 5,
        marginBottom: 3,
    },
});

export default SummaryPDF;
